import React, { Suspense, lazy, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider, useAuth } from './AuthContext';
import { ThemeProvider } from './ThemeContext';
import { ToastProvider } from './ToastContext';
import { ShippingCountProvider } from './ShippingCountContext';
import { GradeRadarProvider } from './GradeRadarContext';
import Sidebar from './components/Sidebar';
import MobileBottomNav from './components/MobileBottomNav';
import PageTour from './components/PageTour';
import AssistantWidget from './components/AssistantWidget';
import SetupWidget from './components/SetupWidget';
import MobileScanFab from './components/MobileScanFab';
import DesktopHint from './components/DesktopHint';
import ErrorBoundary from './components/ErrorBoundary';
import Paywall from './components/Paywall';
import TrialBanner from './components/TrialBanner';
import GrandfatheredBanner from './components/GrandfatheredBanner';
import PaymentFailingBanner from './components/PaymentFailingBanner';
import CookieConsent from './components/CookieConsent';
import ClarityTagger from './components/ClarityTagger';
import axios from 'axios';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import { captureAttribution } from './utils/attribution';
import './App.css';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

// Lazy loaded, only fetched when user navigates there
const Dashboard     = lazy(() => import('./pages/Dashboard'));
const Inventory     = lazy(() => import('./pages/Inventory'));
const AddCard       = lazy(() => import('./pages/AddCard'));
const AddInventory  = lazy(() => import('./pages/AddInventory'));
const EditCard      = lazy(() => import('./pages/EditCard'));
const ScanReview    = lazy(() => import('./pages/ScanReview'));
const MobileScanUpload = lazy(() => import('./pages/MobileScanUpload'));
const Orders        = lazy(() => import('./pages/Orders'));
const Customers     = lazy(() => import('./pages/Customers'));
const Collections   = lazy(() => import('./pages/Collections'));
const Settings      = lazy(() => import('./pages/Settings'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword  = lazy(() => import('./pages/ResetPassword'));
const AcceptInvite   = lazy(() => import('./pages/AcceptInvite'));
const ImportCards   = lazy(() => import('./pages/ImportCards'));
const BulkAddImages = lazy(() => import('./pages/BulkAddImages'));
const Expenses      = lazy(() => import('./pages/Expenses'));
const Consignment   = lazy(() => import('./pages/Consignment'));
const Grading       = lazy(() => import('./pages/Grading'));
const Referral      = lazy(() => import('./pages/Referral'));
const Supplies      = lazy(() => import('./pages/Supplies'));
const ReferralFlyer = lazy(() => import('./pages/ReferralFlyer'));
const Terms         = lazy(() => import('./pages/Terms'));
const Privacy       = lazy(() => import('./pages/Privacy'));
const ConnectShopify = lazy(() => import('./pages/ConnectShopify'));
const Contact       = lazy(() => import('./pages/Contact'));
const FAQ           = lazy(() => import('./pages/FAQ'));
const Guides        = lazy(() => import('./pages/Guides'));
const GuideDealerSoftware = lazy(() => import('./pages/guides/SportsCardDealerSoftware'));
const GuidePsaTracking = lazy(() => import('./pages/guides/PsaSubmissionTracking'));
const GuideAccounting = lazy(() => import('./pages/guides/CardDealerAccounting'));
const GuideConsignment = lazy(() => import('./pages/guides/ConsignmentForCardDealers'));
const GuideConsignmentSoftware = lazy(() => import('./pages/guides/BestSportsCardConsignmentSoftware'));
const GuideStoreCards = lazy(() => import('./pages/guides/HowToStoreSportsCards'));
const GuideSubmitPsa = lazy(() => import('./pages/guides/HowToSubmitToPsa'));
const GuideScannerApp = lazy(() => import('./pages/guides/BestSportsCardScannerApp'));
const GuideInventory = lazy(() => import('./pages/guides/SportsCardInventoryManagement'));
const GuideBulkScan = lazy(() => import('./pages/guides/BulkScanWithScanner'));
const GuideBulkEbay = lazy(() => import('./pages/guides/BulkListCardsOnEbay'));
const GuideGradingWorth = lazy(() => import('./pages/guides/IsGradingWorthIt'));
const GuideScannerHW = lazy(() => import('./pages/guides/BestScannerForSportsCards'));
const GuideNational = lazy(() => import('./pages/guides/SellingAtTheNational'));
const GuideSingles = lazy(() => import('./pages/guides/BuySinglesNotBoxes'));
const GuideJordanCards = lazy(() => import('./pages/guides/HistoryOfMichaelJordanCards'));
const GuideLeBronCards = lazy(() => import('./pages/guides/LeBronJamesRookieCards'));
const GuideCaitlinClarkCards = lazy(() => import('./pages/guides/CaitlinClarkRookieCards'));
const GuideOhtaniCard = lazy(() => import('./pages/guides/OhtaniElevenMillionCard'));
const GuidePriceCards = lazy(() => import('./pages/guides/HowToPriceSportsCards'));
const GuideFlaggCard = lazy(() => import('./pages/guides/CooperFlaggRookiePatchAuto'));
const GuideSpotFakes = lazy(() => import('./pages/guides/HowToSpotFakeSportsCards'));
const GuideSellEbay = lazy(() => import('./pages/guides/HowToSellSportsCardsOnEbay'));
const GuideStartBiz = lazy(() => import('./pages/guides/StartSportsCardBusiness'));
const Guide1952Topps = lazy(() => import('./pages/guides/HistoryOf1952Topps'));
const GuideTaxes = lazy(() => import('./pages/guides/TaxesForSportsCardSellers'));
const GuideCrossList = lazy(() => import('./pages/guides/CrossListSportsCards'));
const GuideCollxAlt = lazy(() => import('./pages/guides/CollxAlternatives'));
const GuideCdpAlt = lazy(() => import('./pages/guides/CardDealerProAlternative'));
const GuidePosSystem = lazy(() => import('./pages/guides/SportsCardPosSystem'));
const GuideCardShop = lazy(() => import('./pages/guides/CardShopSoftware'));
const GuideTCGBiz = lazy(() => import('./pages/guides/TradingCardBusinessSoftware'));
const GuideCardShow = lazy(() => import('./pages/guides/CardShowSoftware'));
const GuideMlbPlayoffs = lazy(() => import('./pages/guides/MlbPlayoffsCardValues'));
const GuideInventoryCompared = lazy(() => import('./pages/guides/BestInventorySoftwareCompared'));
const GuideMostValuable = lazy(() => import('./pages/guides/MostValuableSportsCards'));
const GuideLudexAlt = lazy(() => import('./pages/guides/LudexAlternative'));
const GuideDebutPatch = lazy(() => import('./pages/guides/DebutPatchAutosExplained'));
const GuideWembanyamaCards = lazy(() => import('./pages/guides/VictorWembanyamaRookieCards'));
const FounderMetrics = lazy(() => import('./pages/FounderMetrics'));
const StartTrial    = lazy(() => import('./pages/StartTrial'));
const AcceptableUse = lazy(() => import('./pages/AcceptableUse'));
const PublicCollection = lazy(() => import('./pages/PublicCollection'));
const PublicStore = lazy(() => import('./pages/PublicStore'));
const ConsignorVault = lazy(() => import('./pages/ConsignorVault'));
const Bundles = lazy(() => import('./pages/Bundles'));
const NotFound      = lazy(() => import('./pages/NotFound'));
const Onboarding    = lazy(() => import('./pages/Onboarding'));
const FeedbackSurvey = lazy(() => import('./pages/FeedbackSurvey'));
const SurveyAdmin = lazy(() => import('./pages/SurveyAdmin'));
const Repricing = lazy(() => import('./pages/Repricing'));
const DealAlerts = lazy(() => import('./pages/DealAlerts'));

const GOOGLE_CLIENT_ID = '901177183686-7k1ph8k3hhiism8i3kqahinhpjo3kp8m.apps.googleusercontent.com';

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'var(--text-tertiary)', fontSize: 14 }}>
    Loading…
  </div>
);


function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" />;
  // Card-required trial wall (A/B). The card is asked for AT SIGNUP, before the
  // app, reverted 2026-07-15 from the "after onboarding" placement. That deeper
  // placement (7/13-7/15) took trial STARTS from 4/weekend to ZERO: onboarding
  // became a gate in front of the wall that ~70% quit during, so almost nobody
  // reached the card ask. Card-first is cruder but demonstrably converts (4
  // trials on 7/11 alone). ONLY 'trial'-variant users ever hit this, control +
  // all existing users (free/null) pass straight through. Never gate the wall
  // page itself. (Onboarding still runs, but AFTER they card up.)
  if (user.signupVariant === 'trial' && !user.subscriptionEntitled && location.pathname !== '/start-trial') {
    return <Navigate to="/start-trial" replace />;
  }
  return children;
}

function UpgradeModal() {
  const [show, setShow] = useState(false);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    // Check if we should show the modal (once per session)
    const dismissed = sessionStorage.getItem('scn-upgrade-modal-dismissed');
    if (dismissed) return;

    axios.get(`${API}/stripe/subscription`).then(res => {
      setSub(res.data);
      if (res.data.tier === 'free' && res.data.scansUsed >= res.data.scanLimit) {
        // Delay showing modal by 3 seconds so page loads first
        setTimeout(() => setShow(true), 3000);
      }
    }).catch(() => {});
  }, []);

  if (!show) return null;

  const dismiss = () => { setShow(false); sessionStorage.setItem('scn-upgrade-modal-dismissed', '1'); };

  return (
    <div className="scn-mobile-sheet" style={{ position: 'fixed', inset: 0, background: 'rgba(13,27,62,0.78)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={dismiss}>
      <div className="scn-mobile-sheet-card" onClick={e => e.stopPropagation()} style={{ width: '100%', display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', background: 'transparent' }}>
        <Paywall
          title="You're out of AI credits this month"
          body="Free includes 10 AI credits/month, you've used them all. Upgrade to keep scanning. One click and you're back in."
          features={[
            '100 AI credits per month (Starter), 1,000 on Dealer',
            'AI listing descriptions',
            'Customer CRM + email blast to past buyers',
            'Shipping labels at dealer discount',
            'Expense tracking',
          ]}
          recommendedTier="starter"
        />
        <button onClick={dismiss} style={{ marginTop: 16, padding: '8px 16px', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontFamily: "'Manrope',sans-serif" }}>Maybe later</button>
      </div>
    </div>
  );
}

function AppLayout({ children }) {
  return (
    <ShippingCountProvider>
      <GradeRadarProvider>
      <PageTour />
      <UpgradeModal />
      <div className="app-layout">
        <a href="#main-content" className="skip-nav">Skip to main content</a>
        <Sidebar />
        <main id="main-content" className="app-main">
          <PaymentFailingBanner />
          <TrialBanner />
          <GrandfatheredBanner />
          <DesktopHint />
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </main>
        <MobileBottomNav />
      </div>
      <AssistantWidget />
      <MobileScanFab />
      <SetupWidget />
      </GradeRadarProvider>
    </ShippingCountProvider>
  );
}

const PAGE_TITLES = {
  '/': null, // landing page sets its own title
  '/login': 'Sign In',
  '/register': 'Start Free',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  '/onboarding': 'Get Started',
  '/inventory': 'Inventory',
  '/inventory/add': 'Add Card',
  '/inventory/import': 'Import Cards',
  '/inventory/add-images': 'Bulk Add Images',
  '/inventory/scan-review': 'Scan Review',
  '/accounting': 'Accounting',
  '/consignment': 'Consignment',
  '/grading': 'Grading Tracker',
  '/orders': 'Orders',
  '/customers': 'Customers',
  '/collections': 'Collections',
  '/deal-alerts': 'Card Drop Alerts',
  '/settings': 'Settings',
  '/terms': 'Terms of Service',
  '/privacy': 'Privacy Policy',
  '/contact': 'Contact',
  '/faq': 'FAQ',
  '/guides': 'Guides for Card Dealers',
  '/guides/sports-card-dealer-software': "Best Sports Card Dealer Software (2026): Scan, List & Track Profit",
  '/guides/how-to-track-psa-submissions': 'PSA Submission Tracker (2026): Check Status & Turnaround Times',
  '/guides/how-to-submit-cards-to-psa': 'How to Submit Cards to PSA: Step-by-Step Guide (2026)',
  '/guides/best-sports-card-scanner-app': 'Best Sports Card Scanner App (2026): Scan, Price & List in Seconds',
  '/guides/card-dealer-accounting': 'Card Dealer Accounting (2026): P&L, Taxes, and Real Profit per Card',
  '/guides/consignment-for-card-dealers': 'Consignment for Card Dealers (2026): How to Set Up Splits and Payouts',
  '/guides/best-sports-card-consignment-software': 'Best Sports Card Consignment Software (2026): Splits, Payouts, and a Clean Ledger',
  '/guides/how-to-store-sports-cards': 'How to Store Sports Cards (2026): Raw, Graded, and Bulk the Right Way',
  '/guides/sports-card-inventory-management': 'Sports Card Inventory Management (2026): Track Cards, Costs & Sales in One Place',
  '/guides/bulk-scan-sports-cards': 'How to Bulk-Scan Sports Cards with a Document Scanner (2026)',
  '/guides/how-to-bulk-list-cards-on-ebay': 'How to Bulk List Sports Cards on eBay (2026): The Fast Way for Dealers',
  '/guides/is-grading-worth-it': 'Is Grading Worth It? The ROI Math Card Dealers Use (2026)',
  '/guides/best-scanner-for-sports-cards': 'Best Scanner for Sports Cards (2026): Bulk-Scan Hundreds in Minutes',
  '/guides/selling-at-the-national': "Selling at The National Sports Collectors Convention: A Dealer's Prep Guide (2026)",
  '/guides/buy-singles-not-boxes': "Buy Singles, Not Boxes: How to Start Collecting and Dealing Sports Cards (2026)",
  '/guides/history-of-michael-jordan-cards': 'The History of Michael Jordan Cards (1984 to Today): Rookies, Inserts & Values',
  '/guides/lebron-james-rookie-cards': "LeBron James Rookie Cards: The 2003-04 Rookies That Matter & What They're Worth (2026)",
  '/guides/caitlin-clark-rookie-cards': "Caitlin Clark Rookie Cards: What Matters and What They're Worth (2026)",
  '/guides/ohtani-11-million-card': "Shohei Ohtani's $11 Million Card: Inside the Record Logoman Sale (2026)",
  '/guides/how-to-price-sports-cards': 'How to Price Sports Cards: Comps, Not Guesswork (2026)',
  '/guides/cooper-flagg-rookie-patch-auto': "Cooper Flagg's 1-of-1 Rookie Patch Auto: Inside the Card Already Worth Millions (2026)",
  '/guides/how-to-spot-fake-sports-cards': "How to Spot Fake Sports Cards and Fake PSA Slabs: A Buyer's Checklist (2026)",
  '/guides/how-to-sell-sports-cards-on-ebay': "How to Sell Sports Cards on eBay: A Dealer's Playbook (2026)",
  '/guides/how-to-start-a-sports-card-business': "How to Start a Sports Card Business (2026): A Dealer's Startup Guide",
  '/guides/history-of-1952-topps': "The 1952 Topps Set: The Mickey Mantle #311 and What Actually Drives Its Price (2026)",
  '/guides/taxes-for-sports-card-sellers': "1099-K and Taxes for Sports Card Sellers (2026): What You Actually Owe",
  '/guides/cross-list-sports-cards': "How to Cross-List Sports Cards Across Marketplaces (2026): eBay, Shopify, and More",
  '/guides/collx-alternatives': "CollX Alternatives for Card Dealers (2026): Which Tool Actually Fits",
  '/guides/card-dealer-pro-alternative': "Card Dealer Pro Alternatives (2026): What Fits Beyond Scan-to-eBay",
  '/guides/sports-card-pos-system': "Sports Card POS System (2026): Sell at Shows and In Your Shop",
  '/guides/card-shop-software': "Card Shop Software (2026): Run a Brick-and-Mortar Card Store",
  '/guides/trading-card-business-software': "Trading Card Business Software (2026): Run Your Whole Operation",
  '/guides/card-show-software': "Card Show Software and POS for Dealers (2026): Run Your Table the Right Way",
  '/guides/best-sports-card-inventory-software-compared': "Best Sports Card Inventory Software Compared (2026)",
  '/guides/most-valuable-sports-cards': "The Most Valuable Sports Cards of All Time (2026)",
  '/guides/ludex-alternative': "Ludex Alternatives for Card Dealers (2026): Scan and List, or a Full Back Office",
  '/guides/victor-wembanyama-rookie-cards': "Victor Wembanyama Rookie Cards: The 2023-24 Prizm, the Logoman, and the $5.11 Million Sale (2026)",
  '/guides/debut-patch-autos-explained': "Debut Patch Autographs: Why Four Rookie Cards Just Sold for $14 Million (2026)",
  '/acceptable-use': 'Acceptable Use',
};

// Per-route meta descriptions (titles live in PAGE_TITLES). Client-set, but
// Google renders JS so the guides get their own SERP snippet instead of the
// homepage one. Falls back to the homepage copy on routes without an entry so
// SPA navigation never leaves a stale description behind.
const HOME_DESCRIPTION = 'Scan a card, list it to eBay in one click, and track every sale automatically. Free for up to 200 cards. Built by card dealers, for card dealers.';
const PAGE_DESCRIPTIONS = {
  '/guides': 'In-depth guides for sports card dealers, software, PSA grading, accounting, and consignment.',
  '/guides/sports-card-dealer-software': "Compare the best sports card dealer software for 2026: AI scanning, multi-channel listing, grading ROI, and real profit-and-loss in one flat-priced platform. 14-day free trial.",
  '/guides/how-to-track-psa-submissions': 'Track your PSA submissions and check status in one place: order lookup, the 7 status stages, 2026 turnaround times, and per-card grading ROI. Built for card dealers.',
  '/guides/how-to-submit-cards-to-psa': 'How to submit cards to PSA step by step, choosing a service level, declared value, packaging, shipping, checking submission status, and turnaround times.',
  '/guides/best-sports-card-scanner-app': 'What the best sports card scanner app does for dealers in 2026, accurate identification, live comps, bulk scanning, and one-click eBay listing, with an honest comparison of the options.',
  '/guides/card-dealer-accounting': 'Card dealer accounting made simple, real P&L with actual eBay fees, Schedule C, and 1099-K. What to track and how to file.',
  '/guides/consignment-for-card-dealers': 'How to run consignment as a card dealer, splits, payouts, tracking, and the tools to manage it cleanly.',
  '/guides/best-sports-card-consignment-software': 'How card dealers run consignment without a spreadsheet: per-consignor commission splits, live owed balances, who pays shipping, payout statements, and a consignor portal.',
  '/guides/how-to-store-sports-cards': 'A practical guide to storing sports cards: penny sleeves, toploaders, one-touch holders, graded slab storage, the right temperature and humidity, and the mistakes that ruin cards.',
  '/guides/sports-card-inventory-management': 'Sports card inventory management for dealers, what to track, physical organization and SKUs, cost-basis and real-profit math, cross-platform sync, and when to move off a spreadsheet.',
  '/guides/bulk-scan-sports-cards': 'The fast way to get thousands of cards into inventory: feed a stack through a duplex document scanner, scan to a folder, drop it into SCN, and the AI identifies, pairs front/back, and prices the whole batch. Full scanner setup.',
  '/guides/how-to-bulk-list-cards-on-ebay': "Bulk list sports cards on eBay fast, eBay's own bulk tools and their limits, the item-specifics problem, scan-to-list to kill per-card data entry, titles that get found, comp pricing, and shipping presets.",
  '/guides/is-grading-worth-it': 'Is it worth grading your sports cards? The real answer is per-card ROI, the true all-in cost of grading, the PSA 10-vs-9 premium, why gem rate is the number most people skip, a worked example, turnaround and opportunity cost, and deciding fast at scale.',
  '/guides/best-scanner-for-sports-cards': 'The best scanners for digitizing sports cards fast: an auto-feed document scanner for bulk raw cards, a flatbed for graded slabs, your phone on the go, and the scan-to-list workflow that turns images into listings.',
  '/guides/selling-at-the-national': 'How dealers prep for The National Sports Collectors Convention: catalog and price your table inventory before you go, carry live comps on the floor to buy and sell right, track what moves, and list the leftovers fast after the show. 2026: Rosemont, IL, July 29 to August 2.',
  '/guides/buy-singles-not-boxes': "Why buying single cards beats ripping boxes when you're starting out, and how to go from collector to dealer: pick a focus, buy to real sold comps, track cost basis, and flip the spread.",
  '/guides/history-of-michael-jordan-cards': 'The complete history of Michael Jordan cards: the 1984-85 Star pre-rookie, the iconic 1986-87 Fleer #57 rookie, the 1990s insert era, autographs, and what really drives their value, grade, scarcity, and authenticity.',
  '/guides/lebron-james-rookie-cards': "A dealer's guide to LeBron James rookie cards: the 2003-04 Topps #221 and Topps Chrome #111 rookies, the Refractor and Gold Refractor parallels, the Exquisite Rookie Patch Auto grail, and what drives their value, after the October 2026 $1.63M record sale.",
  '/guides/caitlin-clark-rookie-cards': "A dealer's guide to Caitlin Clark's 2024 rookie cards: the Panini Prizm, Donruss, and Select WNBA base cards, the Logowoman and Signatures 1 of 1 autographs that sold for up to $660,000, and what actually drives their price.",
  '/guides/ohtani-11-million-card': "How Shohei Ohtani's 1-of-1 Dual Gold Logoman autograph sold for $11 million, where it ranks among the biggest sports card sales ever, and what the sale means for collectors and dealers pricing their own cards.",
  '/guides/how-to-price-sports-cards': 'How to price sports cards using real sold comps instead of guesswork: where to find comp data, how to adjust for grade and market timing, and a two-minute pricing workflow dealers actually use.',
  '/guides/cooper-flagg-rookie-patch-auto': "How Cooper Flagg's 1-of-1 Rookie Debut Patch Autograph was pulled and chased by a Dallas Mavericks bounty offer before it even reached auction, what a debut patch auto is, and how it compares to other seven-figure rookie card sales.",
  '/guides/how-to-spot-fake-sports-cards': "How to tell a real sports card from a reprint and a real PSA slab from a fake one: card stock, centering, cert lookups, hologram tests, and what PSA's $200 million fraud report and the Mister Mancave scandal reveal about how counterfeiters operate.",
  '/guides/how-to-sell-sports-cards-on-ebay': "The real eBay fee math for sports cards in 2026, auction vs fixed price vs Best Offer, titles and item specifics that get found, Authenticity Guarantee, Promoted Listings, and how to package and ship without damage.",
  '/guides/how-to-start-a-sports-card-business': "A dealer-to-dealer guide to starting a sports card business in 2026: how much money you need, where to source and where to sell, the real marketplace fees, setting up the business and taxes, and the systems that keep you profitable as you scale.",
  '/guides/history-of-1952-topps': "The history of the 1952 Topps set, why the high-number series is so scarce, and what drives the price of the Mickey Mantle #311, from a $417,450 sale this month to the $12.6 million record.",
  '/guides/taxes-for-sports-card-sellers': "The 1099-K threshold for 2026, why it just reverted to $20,000 and 200 transactions, hobby vs dealer classification, self-employment tax, cost basis, and quarterly estimated payments for sports card sellers.",
  '/guides/cross-list-sports-cards': "How dealers cross-list sports cards across eBay, Shopify, Square, MySlabs, and more from one synced inventory, why the sync matters more than the listing, how to avoid overselling, and how to pick a tool that lists once and de-lists automatically.",
  '/guides/collx-alternatives': "An honest look at CollX alternatives for card dealers in 2026: where CollX shines, where selling dealers outgrow a collector-first app, and how Card Dealer Pro, Ludex, and SportsCardNetwork compare on listing, inventory sync, and accounting.",
  '/guides/card-dealer-pro-alternative': "An honest look at Card Dealer Pro alternatives for 2026: where Card Dealer Pro is strong (fast bulk scan-to-eBay), where dealers need more (multi-channel listing, consignment, accounting), and how CardLuma, Ludex, and SportsCardNetwork compare.",
  '/guides/sports-card-pos-system': "A practical guide to a sports card POS system for 2026: ring up cash and card sales at shows or your shop from the same inventory you list online, so a card sold in person automatically comes off eBay, Shopify, and your other channels, with profit tracked and team logins for the table.",
  '/guides/card-shop-software': "A practical guide to card shop software for 2026: run a brick-and-mortar card store end to end with one inventory that powers your in-store point of sale and online channels, with AI scanning, accounting, consignment, grading ROI, and staff logins.",
  '/guides/trading-card-business-software': "A practical guide to trading card business software for 2026: what it is, what to look for, and how to run intake, multi-channel listing, inventory sync, accounting, consignment, grading, and in-person sales from one place.",
  '/guides/card-show-software': "How dealers run a card show table with software: pricing inventory before the show, taking payment with a point of sale and Square, avoiding selling a card that already sold online, and keeping every in-person sale in your real profit-and-loss.",
  '/guides/best-sports-card-inventory-software-compared': "An honest comparison of the main sports card inventory platforms in 2026: SportsCardNetwork, Card Dealer Pro, Slabfy, and Card Ladder, with what actually separates them and how to pick by card count and sales channels.",
  '/guides/most-valuable-sports-cards': "The confirmed record sales behind the most valuable sports cards ever sold: the $12.932 million Jordan/Bryant Logoman, the $12.6 million 1952 Topps Mantle, the $7.25 million T206 Wagner, and where recent sales like Ohtani's $11 million card actually rank.",
  '/guides/ludex-alternative': "An honest look at Ludex alternatives for card dealers in 2026: where Ludex is strong at scanning, pricing, and its List-It eBay feature, where selling dealers need more, and how CollX, Card Dealer Pro, and SportsCardNetwork compare on multi-channel listing, consignment, and accounting.",
  '/guides/victor-wembanyama-rookie-cards': "A dealer's guide to Victor Wembanyama's rookie cards: why none of them are autographed, the 2023-24 Panini Prizm #136 parallel ladder, the National Treasures Logoman, and the record $5.11 million Prizm Black 1 of 1 sale.",
  '/guides/debut-patch-autos-explained': "Cooper Flagg, Dylan Harper, Kon Knueppel, and Pete Crow-Armstrong's Rookie Debut Patch Autographs sold for a combined $14.26 million in late September 2026. What a debut patch auto is, why basketball jumped past baseball, and what the grade spread between the cards says about pricing your own.",
  '/faq': 'Answers about SportsCardNetwork, pricing, eBay import, PSA grading, accounting, and the free plan for card dealers.',
};

function usePageTitle() {
  const location = useLocation();
  React.useEffect(() => {
    // Scroll to the top on every page change, without this the new page keeps
    // the previous page's scroll position, cutting off its header. Keyed on
    // pathname (not search) so ?tab= switches don't yank you to the top.
    window.scrollTo(0, 0);
    const path = location.pathname;
    const title = PAGE_TITLES[path];
    if (title) {
      document.title = `${title}: SportsCardNetwork`;
    } else if (path === '/') {
      document.title = 'SportsCardNetwork: List Cards to eBay in One Click';
    }
    // Keep the meta description in sync per route (falls back to home copy).
    const desc = PAGE_DESCRIPTIONS[path] || HOME_DESCRIPTION;
    const tag = document.querySelector('meta[name="description"]');
    if (tag) tag.setAttribute('content', desc);
    // Canonical must follow the route too. index.html hardcodes the homepage
    // URL, and nothing was updating it, so every prerendered guide and legal
    // page told Google it was a duplicate of the homepage. That is an
    // instruction to index the homepage INSTEAD of the article, which quietly
    // works against the whole SEO effort. Verified live 2026-08-24: /guides/*,
    // /faq and /privacy all served canonical=https://www.sportscardnetwork.ai/.
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://www.sportscardnetwork.ai' + (path === '/' ? '/' : path));
    }
  }, [location.pathname]);
}

// When an already-logged-in user lands on an auth route, honor a safe ?next
// (used by the team-invite accept flow so sign-in returns to /accept-invite
// instead of racing to "/"). Internal relative paths only, no open redirect.
function authRedirectTarget(fallback) {
  try {
    const n = new URLSearchParams(window.location.search).get('next');
    if (n && n.startsWith('/') && !n.startsWith('//')) return n;
  } catch { /* ignore */ }
  return fallback;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  usePageTitle();
  useEffect(() => { captureAttribution(); }, []);
  // Counterpart of the prerender anti-flash guard (client/prerender.js): the
  // prerendered markup was hidden because the URL didn't match it; React has
  // now painted its own tree (loader or page), so reveal #root.
  useEffect(() => { document.documentElement.classList.remove('scn-prerender-hide'); }, []);
  // Finish a pending Shopify-initiated install after the merchant authenticates:
  // ConnectShopify stashed the link token before bouncing them through login/signup,
  // so once they're in, route them back to claim their store.
  useEffect(() => {
    if (!user) return;
    try {
      if (localStorage.getItem('scn_pending_shopify_link') && window.location.pathname !== '/connect-shopify') {
        navigate('/connect-shopify');
      }
    } catch { /* ignore */ }
  }, [user, navigate]);
  if (loading) return <PageLoader />;

  return (
    <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={
          !user ? <LandingPage />
          // Card-required trial wall (A/B), same gate as ProtectedRoute, since
          // "/" bypasses it. Fires at SIGNUP (reverted 7/15): the card ask comes
          // before onboarding, so it must win over the onboarding redirect below.
          : (user.signupVariant === 'trial' && !user.subscriptionEntitled) ? <Navigate to="/start-trial" replace />
          : (() => {
          // Server flag wins: if user.onboardedAt is set, they've completed onboarding
          // on any device. Falls back to legacy localStorage flag for users who
          // onboarded before the server-flag rollout, they get migrated on next
          // onboarding action. New users only get the server flag.
          if (user.onboardedAt) return false;
          try { return localStorage.getItem('scn-onboarded') !== '1'; } catch { return true; }
        })() ? <Navigate to="/onboarding" replace /> : <AppLayout><Dashboard /></AppLayout>} />

        {/* Public routes */}
        <Route path="/login" element={user ? <Navigate to={authRedirectTarget('/')} /> : <Login />} />
        <Route path="/register" element={user ? <Navigate to={authRedirectTarget('/onboarding')} /> : <Register />} />
        <Route path="/forgot-password" element={user ? <Navigate to="/" /> : <ForgotPassword />} />
        <Route path="/reset-password" element={user ? <Navigate to="/" /> : <ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />

        {/* Onboarding */}
        <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
        <Route path="/start-trial" element={<ProtectedRoute><StartTrial /></ProtectedRoute>} />
        <Route path="/founder" element={<ProtectedRoute><FounderMetrics /></ProtectedRoute>} />

        {/* Legal & public pages */}
        <Route path="/collection/:token" element={<PublicCollection />} />
        <Route path="/store/:token" element={<PublicStore />} />
        <Route path="/vault/:token" element={<ConsignorVault />} />
        <Route path="/m/scan/:sessionId" element={<MobileScanUpload />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/connect-shopify" element={<ConnectShopify />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/guides" element={<Guides />} />
        <Route path="/guides/sports-card-dealer-software" element={<GuideDealerSoftware />} />
        <Route path="/guides/how-to-track-psa-submissions" element={<GuidePsaTracking />} />
        <Route path="/guides/how-to-submit-cards-to-psa" element={<GuideSubmitPsa />} />
        <Route path="/guides/best-sports-card-scanner-app" element={<GuideScannerApp />} />
        <Route path="/guides/card-dealer-accounting" element={<GuideAccounting />} />
        <Route path="/guides/consignment-for-card-dealers" element={<GuideConsignment />} />
        <Route path="/guides/best-sports-card-consignment-software" element={<GuideConsignmentSoftware />} />
        <Route path="/guides/how-to-store-sports-cards" element={<GuideStoreCards />} />
        <Route path="/guides/sports-card-inventory-management" element={<GuideInventory />} />
        <Route path="/guides/bulk-scan-sports-cards" element={<GuideBulkScan />} />
        <Route path="/guides/how-to-bulk-list-cards-on-ebay" element={<GuideBulkEbay />} />
        <Route path="/guides/is-grading-worth-it" element={<GuideGradingWorth />} />
        <Route path="/guides/best-scanner-for-sports-cards" element={<GuideScannerHW />} />
        <Route path="/guides/selling-at-the-national" element={<GuideNational />} />
        <Route path="/guides/buy-singles-not-boxes" element={<GuideSingles />} />
        <Route path="/guides/history-of-michael-jordan-cards" element={<GuideJordanCards />} />
        <Route path="/guides/lebron-james-rookie-cards" element={<GuideLeBronCards />} />
        <Route path="/guides/caitlin-clark-rookie-cards" element={<GuideCaitlinClarkCards />} />
        <Route path="/guides/ohtani-11-million-card" element={<GuideOhtaniCard />} />
        <Route path="/guides/how-to-price-sports-cards" element={<GuidePriceCards />} />
        <Route path="/guides/cooper-flagg-rookie-patch-auto" element={<GuideFlaggCard />} />
        <Route path="/guides/how-to-spot-fake-sports-cards" element={<GuideSpotFakes />} />
        <Route path="/guides/how-to-sell-sports-cards-on-ebay" element={<GuideSellEbay />} />
        <Route path="/guides/how-to-start-a-sports-card-business" element={<GuideStartBiz />} />
        <Route path="/guides/history-of-1952-topps" element={<Guide1952Topps />} />
        <Route path="/guides/taxes-for-sports-card-sellers" element={<GuideTaxes />} />
        <Route path="/guides/cross-list-sports-cards" element={<GuideCrossList />} />
        <Route path="/guides/collx-alternatives" element={<GuideCollxAlt />} />
        <Route path="/guides/card-dealer-pro-alternative" element={<GuideCdpAlt />} />
        <Route path="/guides/sports-card-pos-system" element={<GuidePosSystem />} />
        <Route path="/guides/card-shop-software" element={<GuideCardShop />} />
        <Route path="/guides/trading-card-business-software" element={<GuideTCGBiz />} />
        <Route path="/guides/card-show-software" element={<GuideCardShow />} />
        <Route path="/guides/how-mlb-playoffs-affect-card-values" element={<GuideMlbPlayoffs />} />
        <Route path="/guides/best-sports-card-inventory-software-compared" element={<GuideInventoryCompared />} />
        <Route path="/guides/most-valuable-sports-cards" element={<GuideMostValuable />} />
        <Route path="/guides/ludex-alternative" element={<GuideLudexAlt />} />
        <Route path="/guides/victor-wembanyama-rookie-cards" element={<GuideWembanyamaCards />} />
        <Route path="/guides/debut-patch-autos-explained" element={<GuideDebutPatch />} />
        <Route path="/acceptable-use" element={<AcceptableUse />} />

        {/* Protected routes */}
        <Route path="/inventory" element={<ProtectedRoute><AppLayout><Inventory /></AppLayout></ProtectedRoute>} />
        <Route path="/inventory/add-inventory" element={<ProtectedRoute><AppLayout><AddInventory /></AppLayout></ProtectedRoute>} />
        <Route path="/inventory/add" element={<ProtectedRoute><AppLayout><AddCard /></AppLayout></ProtectedRoute>} />
        <Route path="/inventory/edit/:id" element={<ProtectedRoute><AppLayout><EditCard /></AppLayout></ProtectedRoute>} />
        <Route path="/inventory/import" element={<ProtectedRoute><AppLayout><ImportCards /></AppLayout></ProtectedRoute>} />
        <Route path="/inventory/add-images" element={<ProtectedRoute><AppLayout><BulkAddImages /></AppLayout></ProtectedRoute>} />
        <Route path="/inventory/scan-review" element={<ProtectedRoute><AppLayout><ScanReview /></AppLayout></ProtectedRoute>} />
        <Route path="/accounting" element={<ProtectedRoute><AppLayout><Expenses /></AppLayout></ProtectedRoute>} />
        <Route path="/consignment" element={<ProtectedRoute><AppLayout><Consignment /></AppLayout></ProtectedRoute>} />
        <Route path="/grading" element={<ProtectedRoute><AppLayout><Grading /></AppLayout></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><AppLayout><Orders /></AppLayout></ProtectedRoute>} />
        {/* /documents was an orphan page, no nav surfaced it, and every tab
            inside it duplicated Accounting (Financials = Overview; Consignment
            and Reports already redirected). Redirect bookmarks straight to
            Accounting so the URL still works, but nothing renders Documents
            anymore. Page component deleted in the same commit. */}
        <Route path="/documents" element={<Navigate to="/accounting" replace />} />
        <Route path="/customers" element={<ProtectedRoute><AppLayout><Customers /></AppLayout></ProtectedRoute>} />
        <Route path="/collections" element={<ProtectedRoute><AppLayout><Collections /></AppLayout></ProtectedRoute>} />
        <Route path="/bundles" element={<ProtectedRoute><AppLayout><Bundles /></AppLayout></ProtectedRoute>} />
        <Route path="/repricing" element={<ProtectedRoute><AppLayout><Repricing /></AppLayout></ProtectedRoute>} />
        <Route path="/deal-alerts" element={<ProtectedRoute><AppLayout><DealAlerts /></AppLayout></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><AppLayout><Settings /></AppLayout></ProtectedRoute>} />
        <Route path="/referral" element={<ProtectedRoute><AppLayout><Referral /></AppLayout></ProtectedRoute>} />
        <Route path="/supplies" element={<ProtectedRoute><AppLayout><Supplies /></AppLayout></ProtectedRoute>} />
        <Route path="/referral/flyer" element={<ProtectedRoute><ReferralFlyer /></ProtectedRoute>} />
        <Route path="/feedback" element={<ProtectedRoute><AppLayout><FeedbackSurvey /></AppLayout></ProtectedRoute>} />
        <Route path="/admin/surveys" element={<ProtectedRoute><AppLayout><SurveyAdmin /></AppLayout></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

function App() {
  // The ErrorBoundary auto-reloads once on a stale-chunk error (new deploy),
  // guarded by a sessionStorage flag so it can't loop. Clear that flag after a
  // clean run so the NEXT deploy also auto-recovers instead of showing the
  // manual "Update available" screen. The delay means a page that fails
  // immediately after reload won't clear it (loop protection stays intact).
  useEffect(() => {
    const t = setTimeout(() => { try { sessionStorage.removeItem('__chunk_reload'); } catch {} }, 8000);
    return () => clearTimeout(t);
  }, []);
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ThemeProvider>
        <ToastProvider>
          <Router>
            <AuthProvider>
              <CookieConsent />
              <ClarityTagger />
              <AppRoutes />
            </AuthProvider>
          </Router>
        </ToastProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
